import {API_URL} from "../../../constants";
import {makeRequest} from "../../../services/requestService";
import {useTranslation} from "react-i18next";
import React from "react";
import {
    Box, Button,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField
} from "@mui/material";
import * as PropTypes from "prop-types";

async function fetchRoles() {
    const url = `${API_URL}/roles`;
    const response = await makeRequest(url);

    if (response.status !== 200) {
        throw response;
    }

    return response.data;
}

function arrayToMap(arr, key = "id") {
    return arr.reduce((acc, cur) => acc.set(cur[key], cur), new Map());
}

async function updateUser(user, roles) {
    const url = `${API_URL}/user/${user.id}`;

    const body = {
        username: user.name,
        displayName: user.displayName,
        email: user.email,
        roles,
    }

    const response = await makeRequest(url, "PUT", body, {
        'Content-Type': 'application/json'
    });
    return response.data;
}

export default function GeneralTab(props) {
    const {t} = useTranslation("common")
    const [disabled, setDisabled] = React.useState(true);
    const [roles, setRoles] = React.useState(new Map());
    const [userRoles, setUserRoles] = React.useState(props.user.roles.map((role) => role.id));
    const [user, setUser] = React.useState({...props.user});

    React.useEffect(async () => {
        const roles = await fetchRoles();
        setRoles(arrayToMap(roles));
    }, []);

    function handleUserRolesChange(event) {
        const value = event.target.value;
        if (typeof value === "string") {
            setUserRoles(value.split(",").map((val) => parseInt(val.trim())));
        } else {
            setUserRoles(value);
        }
    }

    async function handleSave() {
        const updatedUser = await updateUser(user, userRoles);
        if (props.onUserUpdate) {
            props.onUserUpdate(updatedUser);
        }
    }

    function handleReset() {
        setUserRoles(props.user.roles.map((role) => role.id));
        setUser({...props.user});
    }

    return (<Stack>
        <Box display="flex" justifyContent="flex-end" mb={2}>
            <FormControlLabel
                control={
                    <Switch value={!disabled}
                            onChange={(_, checked) => setDisabled(!checked)}/>
                } label={t('main.pages.admin.user.tabs.general.edit')}
            />
        </Box>
        <form>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label={t('main.components.form.placeholder.username')}
                        value={user.name}
                        onChange={(event) => setUser({...user, name: event.target.value})}
                        disabled={disabled}/>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label={t('main.components.form.placeholder.pseudo')}
                        value={user.displayName}
                        onChange={(event) => setUser({...user, displayName: event.target.value})}
                        disabled={disabled}/>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        label={t('main.components.form.placeholder.email')}
                        type="email"
                        value={user.email}
                        onChange={(event) => setUser({...user, email: event.target.value})}
                        disabled={disabled}/>
                </Grid>
                <Grid item xs={6}>
                    <Select
                        multiple
                        fullWidth
                        displayEmpty
                        value={userRoles}
                        disabled={disabled}
                        onChange={handleUserRolesChange}
                        sx={{
                            '& .MuiSelect-select': userRoles.length > 0 ? {
                                paddingTop: "11.5px", paddingBottom: "11.5px",
                            } : {},
                        }}
                        renderValue={(selected) => {
                            if (selected.length === 0) {
                                return <em>{t('main.pages.admin.user.tabs.general.roles.placeholder')}</em>
                            }
                            return <Box display="flex" flex="wrap" sx={{gap: 0.5}}>{selected.map((v) => <Chip
                                key={v}
                                label={t(`common.user.roles.${roles.get(v)?.name.toLowerCase()}`)}/>)}</Box>;
                        }}
                    >
                        <MenuItem disabled value="">
                            <em>{t('main.pages.admin.user.tabs.general.roles.placeholder')}</em>
                        </MenuItem>
                        {[...roles.values()].map((role, index) => (<MenuItem key={index} value={role.id}>
                            <Checkbox checked={userRoles.indexOf(role.id) > -1}/>
                            <ListItemText primary={t(`common.user.roles.${role.name.toLowerCase()}`)}/>
                        </MenuItem>))}
                    </Select>
                </Grid>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button type="reset" variant="outlined" onClick={() => handleReset()} disabled={disabled}>
                            {t("main.components.button.reset")}
                        </Button>
                        <Button type="submit" variant="contained" onClick={() => handleSave()} disabled={disabled}>
                            {t("main.components.button.save")}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </form>
    </Stack>);
}

GeneralTab.propTypes = {
    user: PropTypes.object.isRequired,
    onUserUpdate: PropTypes.func,
}
